import React, { useCallback, useState, ReactNode } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AuthError } from './keycloak/login';
import { useAuth, useLogin } from './hooks';

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 300px;

  input {
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    display: inline-block;
    margin: 8px 0;
    padding: 12px 20px;
  }
`;

const StyledButton = styled.button`
  background-color: rgb(0, 123, 194);
  border: none;
  color: #fff;
  cursor: pointer;
  margin: 8px 0;

  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  border-radius: 40px;
  box-shadow: unset;
  height: 40px;
  min-width: 32px;
  padding: 8px 20px;
  text-transform: none;

  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  &:hover,
  &:focus {
    background-color: rgb(0, 97, 153);
  }
`;

function LoginComponent() {
  const navigate = useNavigate();
  const login = useLogin();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');

  const loginWrapper = useCallback(
    async (username: string, password: string) => {
      try {
        await login(username, password);
        return true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        const error = e.response?.data?.error;
        if (error === 'BAD_CREDENTIALS') {
          setError('Invalid username or password');
          return false;
        }

        if (!(e instanceof AuthError)) {
          throw e;
        }

        if (e.errorCode === 'invalid_grant') {
          setError('Invalid username or password');
          return false;
        }

        setError('Something went wrong');
        console.log(e);

        return false;
      }
    },
    [login],
  );

  return (
    <LoginForm>
      {error && <span style={{ color: 'red' }}>{error}</span>}
      <input
        type="text"
        value={username}
        placeholder="username"
        onChange={(event) => {
          setUsername(event.target.value);
          setError('');
        }}
      />
      <input
        type="password"
        value={password}
        placeholder="password"
        onChange={(event) => {
          setPassword(event.target.value);
          setError('');
        }}
      />
      <StyledButton
        type="button"
        onClick={() => {
          setError('');
          void loginWrapper(username, password).then(() => navigate(0));
        }}
      >
        Login
      </StyledButton>
    </LoginForm>
  );
}

const StyledToolbarButton = styled(StyledButton)`
  background-color: transparent;
  color: rgb(0, 123, 194);
  position: absolute;
  top: 0px;
  z-index: 10;
  margin: 4px 0;

  &:hover,
  &:focus {
    background-color: rgba(0, 123, 194, 0.15);
    color: rgb(0, 97, 153);
  }
`;

const LogoutComponent = (): JSX.Element | null => {
  const navigate = useNavigate();
  const auth = useAuth(true);

  return (
    <StyledToolbarButton
      style={{ right: 4 }}
      onClick={() => {
        void auth.logout();
        navigate(0);
      }}
    >
      Logout
    </StyledToolbarButton>
  );
};

const CopyTokenToClipboardComponent = (): JSX.Element | null => {
  const auth = useAuth(true);

  return (
    <StyledToolbarButton
      style={{ left: 4 }}
      onClick={() => {
        const token = auth.token();
        void navigator.clipboard.writeText('Bearer ' + token);
      }}
    >
      Copy Token to Clipboard
    </StyledToolbarButton>
  );
};

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  height: calc(100vh - 16px);
  overflow: hidden;
  transition: width 0.2s ease-out 0s;
  background: rgb(244, 246, 248);
`;

const StyledToolbar = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  width: 100%;
  height: 50px;
  min-height: 50px;
  padding: 8px;
  z-index: 10;
  background: rgb(244, 246, 248);
`;

const StyledMain = styled.div`
  flex-grow: 1;
  overflow: auto;
  background: rgb(244, 246, 248);
`;

const LoggedInLayout = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element | null => {
  return (
    <StyledLayout>
      <StyledToolbar>
        <CopyTokenToClipboardComponent />
        <LogoutComponent />
      </StyledToolbar>
      <StyledMain>{children}</StyledMain>
    </StyledLayout>
  );
};

const AdminLoggedInLayout = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element | null => {
  return (
    <StyledLayout>
      <StyledToolbar>
        <LogoutComponent />
      </StyledToolbar>
      <StyledMain>{children}</StyledMain>
    </StyledLayout>
  );
};

const StandaloneLogin = {
  LoginComponent,
  LogoutComponent,
  CopyTokenToClipboardComponent,
  LoggedInLayout,
  AdminLoggedInLayout,
};

export { StandaloneLogin };
